
import { supabase } from '@/lib/supabase';

// Force production API - avoid localhost issues in development
const API_URL = 'https://mobile.padrinho.app';

export interface Clinic {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  website?: string;
  description?: string;
  logo_path?: string;
  schedule?: any;
  whatsapp?: string;
  instagram?: string;
  facebook?: string;
  admin_user_id?: string;
  is_active: boolean;
  sync_status?: string;
  synced_at?: string;
  sync_error?: string;
  last_sync_attempt?: string;
  sync_retry_count?: number;
  created_by?: number;
  updated_by?: number;
  created_at: string;
  updated_at: string;
}

/**
 * Gets the current access token from Supabase session
 * NOTE: This may hang if called before auth is ready. Prefer passing token from caller.
 */
const getAccessToken = async (): Promise<string | null> => {
  try {
    console.log('🔍 getAccessToken: Getting session...');
    const { data: { session } } = await supabase.auth.getSession();
    console.log('🔍 getAccessToken: Session retrieved, has token:', !!session?.access_token);
    return session?.access_token || null;
  } catch (error) {
    console.error('🔍 getAccessToken: Error getting session:', error);
    return null;
  }
};

/**
 * Fetches clinic data for the current user's associated clinic via API
 * Uses the secure API endpoint which bypasses RLS timing issues
 * @param providedToken - Optional access token from caller (recommended to avoid getSession hang)
 */
export const fetchUserClinic = async (providedToken?: string): Promise<Clinic | null> => {
  try {
    console.log('🔍 fetchUserClinic: Starting clinic fetch via API...');
    console.log('🔍 fetchUserClinic: API_URL =', API_URL);
    console.log('🔍 fetchUserClinic: Token provided by caller:', !!providedToken);

    // Use provided token or fall back to getSession
    let accessToken = providedToken;
    if (!accessToken) {
      console.log('🔍 fetchUserClinic: No token provided, getting from session...');
      accessToken = await getAccessToken() || undefined;
    }
    console.log('🔍 fetchUserClinic: Access token available:', !!accessToken);
    
    if (!accessToken) {
      console.error('🔍 fetchUserClinic: No access token available');
      throw new Error('User not authenticated');
    }

    // First, get the user's profile to get their clinic_id
    console.log('🔍 fetchUserClinic: Fetching profile from', `${API_URL}/api/auth/profile/me`);
    const profileResponse = await fetch(`${API_URL}/api/auth/profile/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      }
    });
    console.log('🔍 fetchUserClinic: Profile response status:', profileResponse.status);

    if (!profileResponse.ok) {
      const errorText = await profileResponse.text();
      console.error('🔍 fetchUserClinic: Profile fetch failed:', profileResponse.status, errorText);
      throw new Error('Failed to fetch user profile');
    }

    const profileResult = await profileResponse.json();
    console.log('🔍 fetchUserClinic: Profile result:', { 
      success: profileResult.success, 
      hasProfile: profileResult.hasProfile,
      clinicId: profileResult.profile?.clinic_id 
    });

    if (!profileResult.success || !profileResult.profile?.clinic_id) {
      console.error('🔍 fetchUserClinic: No clinic_id found in profile');
      throw new Error('User has no associated clinic');
    }

    const clinicId = profileResult.profile.clinic_id;
    console.log('🔍 fetchUserClinic: Fetching clinic ID:', clinicId);

    // Now fetch the clinic data using the API endpoint
    console.log('🔍 fetchUserClinic: Fetching clinic from', `${API_URL}/api/clinic/${clinicId}`);
    const clinicResponse = await fetch(`${API_URL}/api/clinic/${clinicId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      }
    });
    console.log('🔍 fetchUserClinic: Clinic response status:', clinicResponse.status);

    if (!clinicResponse.ok) {
      const errorText = await clinicResponse.text();
      console.error('🔍 fetchUserClinic: Clinic fetch failed:', clinicResponse.status, errorText);
      throw new Error('Failed to fetch clinic data');
    }

    const clinicResult = await clinicResponse.json();
    console.log('🔍 fetchUserClinic: Clinic result:', { 
      success: clinicResult.success, 
      name: clinicResult.clinic?.name 
    });

    if (!clinicResult.success || !clinicResult.clinic) {
      throw new Error('Clinic not found or inactive');
    }

    console.log('🔍 fetchUserClinic: Clinic found successfully via API:', clinicResult.clinic.name);
    return clinicResult.clinic;

  } catch (error) {
    console.error('🔍 fetchUserClinic: Error fetching user clinic:', error);
    throw error;
  }
};

/**
 * Fetches clinic data by clinic ID via API
 * Uses the secure API endpoint which bypasses RLS
 */
export const fetchClinicById = async (clinicId: number): Promise<Clinic | null> => {
  try {
    console.log('🏥 fetchClinicById: Fetching clinic ID:', clinicId);

    const accessToken = await getAccessToken();
    if (!accessToken) {
      console.error('🏥 fetchClinicById: No access token available');
      throw new Error('User not authenticated');
    }

    const response = await fetch(`${API_URL}/api/clinic/${clinicId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      }
    });

    if (!response.ok) {
      console.error('🏥 fetchClinicById: Clinic fetch failed:', response.status);
      throw new Error('Failed to fetch clinic data');
    }

    const result = await response.json();
    
    if (!result.success || !result.clinic) {
      console.error('🏥 fetchClinicById: Clinic not found');
      throw new Error('Clinic not found');
    }

    console.log('🏥 fetchClinicById: Clinic found:', result.clinic.name);
    return result.clinic;

  } catch (error) {
    console.error('🏥 fetchClinicById: Error fetching clinic by ID:', error);
    throw error;
  }
};

/**
 * Updates clinic information
 * Note: This is an admin function - consider adding an API endpoint if needed
 */
export const updateClinic = async (clinicId: number, updates: Partial<Clinic>): Promise<Clinic | null> => {
  try {
    const { data: clinic, error } = await supabase
      .from('clinics')
      .update(updates)
      .eq('id', clinicId)
      .select()
      .single();

    if (error) {
      console.error('Clinic update error:', error);
      throw new Error('Failed to update clinic data');
    }

    return clinic;

  } catch (error) {
    console.error('Error updating clinic:', error);
    throw error;
  }
};

/**
 * Creates a new clinic
 * Note: This is an admin function - consider adding an API endpoint if needed
 */
export const createClinic = async (clinicData: Omit<Clinic, 'id' | 'created_at' | 'updated_at'>): Promise<Clinic | null> => {
  try {
    const { data: clinic, error } = await supabase
      .from('clinics')
      .insert(clinicData)
      .select()
      .single();

    if (error) {
      console.error('Clinic creation error:', error);
      throw new Error('Failed to create clinic');
    }

    return clinic;

  } catch (error) {
    console.error('Error creating clinic:', error);
    throw error;
  }
};

/**
 * Links a user to a clinic
 * Note: This is an admin function - consider adding an API endpoint if needed
 */
export const linkUserToClinic = async (userId: string, clinicId: number): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ clinic_id: clinicId })
      .eq('id', userId);

    if (error) {
      console.error('User-clinic link error:', error);
      throw new Error('Failed to link user to clinic');
    }

    return true;

  } catch (error) {
    console.error('Error linking user to clinic:', error);
    throw error;
  }
};

/**
 * Gets all users linked to a clinic (for admin use)
 * Note: This is an admin function - consider adding an API endpoint if needed
 */
export const getClinicUsers = async (clinicId: number): Promise<any[]> => {
  try {
    const { data: users, error } = await supabase
      .from('profiles')
      .select('id, email, full_name, created_at')
      .eq('clinic_id', clinicId);

    if (error) {
      console.error('Clinic users fetch error:', error);
      throw new Error('Failed to fetch clinic users');
    }

    return users || [];

  } catch (error) {
    console.error('Error fetching clinic users:', error);
    throw error;
  }
};
